import {AbstractQITMeetingState, AbstractState} from "./DHTStates";

export class AfterInterviewsState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Interviews";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I know that we are not reaching all of the children in this community with immunization services. I would be interested in ideas and tools that would help us do better.”
        </p>
        <p class="my-3">This facility manager is interested in the quality improvement teams and wants to learn more.</p>`
      });
    }
    if (stakeholder.id === "vaccinatorA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “Of course it would be better to reach more children, but we do not have the resources!”
        </p>
        <p class="my-3">
          This vaccinator is interested in the change. He has ideas for reaching additional communities and children, but doesn’t feel like he currently has the resources and authority to do so.
        </p>`
      });
    }
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: `<p class="my-3.leading-tight">
          Overall, the immunization services at this facility are highly regarded and well utilized by the community. Most parents understand the importance of immunization and bring their children to the facility to receive vaccinations, even when they need to travel some distance to do so. 
        </p>`
      });
    }
  }
}

export class FromDataAnalysisState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Data Analysis";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “The data showed what I suspected; we could do a better job of reaching children in nomadic communities.”
        </p>`
      })
    }
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0">
          You meet with the manager at this facility to review its data together.<br/><br/>
          Overall, this facility has <span class="border-b-2 border-dotted border-gray-800" data-tippy-content="For more information about analyzing facility data based on access and utilization, see the Resources section.">
          good access to and good utilization of routine immunization services.</span><br/><br/>
          However, a nomadic community in this area is under-reached by immunization services. Parents do not
          seem opposed to immunization, but it is seen as less of a priority, especially when the community is
          further from the facility. Children who receive initial vaccinations may or may not return for follow-up.<br/><br/>
          A leader within the nomadic community seems somewhat interested working with the facility staff to better reach children in the community.
        </p>`
      })
    }
  }
}

export class GoAndSeeState extends AbstractState {
  getStakeholderState (stakeholder) {}
}

export class QITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes()
      })
    }
  }
}

export class FirstSuccessfulQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “That meeting went really well!”
        </p>`
      })
    }
    if (stakeholder.id === "vaccinatorA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “It seems like we have a plan, but I wonder how the outreach session will go.”
        </p>`
      })
    }
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
        <p class="my-5 font-bold">
          The facility sends the following additional notes about the meeting:
        </p>
        <ul class="my-5 lg:my-0 list-disc list-inside">
          <li>
            A leader from the nomadic community attended the meeting
          </li>
          <li>
            We used the <span class="border-b-2 border-dotted border-gray-800" data-tippy-content="For more information about these and other problem solving tools, see the Resources section.">fish-bone diagram and 5-whys</span> to
            determine that a root cause of poor access in this community is that parents are unable to travel to
            the facility at certain times of the year, when they are farther away. Even though we have tried
            offering outreach sessions to address this in the past, they have not been well attended.
          </li>
          <li>
            The community leader suggested that we try offering outreach sessions immediately after a regular
            community gathering.
          </li>
          <li>
            We agreed to try this once per month, as our first <span class="whitespace-no-wrap border-b-2 border-dotted border-gray-800" data-tippy-content="PDSA stands for Plan Do Study Act. For more information about this and other problem solving tools, see the Resources section.">PDSA cycle</span>,
            and he agreed to recruit volunteers to spread the word about the session.
          </li>
        </ul>`
      })
    }
  }
}

export class FollowupQITMeetingState extends AbstractQITMeetingState {
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: this.getGenericQITNotes() + `
        <p class="my-5 font-bold">
          The facility sends the following additional notes about the meeting:
        </p>
        <ul class="my-5 lg:my-0 list-disc list-inside">
          <li>
            The initial outreach session went well. However, we had concerns about not having the funds to regularly transport the vaccinator to these sessions.
          </li>
          <li>
            Community leader has suggested that a transport that the community uses for supplies can also be used for vaccinator.
          </li>
        </ul>`
      })
    }
  }
}

export class MicroPlanningMeetingState extends AbstractQITMeetingState {
  getTitle() {
    return "Week " + this.getWeek() + ": Micro Planning";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: this.getMicroPlanningNotes()
      })
    }
  }
}

export class WhenReaches80PercentAdoptionState extends AbstractState {
  getTitle() {
    return super.getTitle() + ": Adoption";
  }
  getStakeholderState (stakeholder) {
    if (stakeholder.id === "managerA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I can really see this working at our facility. I am pleased with the number of additional children we have reached, and I think that we will continue to improve.”
        </p>`
      })
    }
    if (stakeholder.id === "vaccinatorA") {
      return super.getStakeholderState({
        data: `<p class="my-3 lg:my-0 text-brand-blue-600 text-xl lg:text-2xl italic leading-snug lg:w-5/6">
          “I have learned so much through the processes of being a part of the quality improvement team.”
        </p>`
      })
    }
    if (stakeholder.id === "communityA") {
      return super.getStakeholderState({
        data: "This facility is now regularly using quality improvement teams for problem solving and more effective micro planning, with the active input of the community."
      })
    }
  }
}
